import { deriveVerticesFromExactEdgeLoop } from "./editSelection.ts";
import {
  edgeVertices,
  findEdgeIndex,
  resolveTopologyOpSelection,
} from "./topology.ts";
import type { PolygonTopology } from "./types.ts";

export interface TopologyOpPreflightSuccess<T> {
  ok: true;
  target: T;
}

export interface TopologyOpPreflightFailure {
  ok: false;
  reason: string;
}

export type TopologyOpPreflight<T> =
  | TopologyOpPreflightSuccess<T>
  | TopologyOpPreflightFailure;

export interface FillSelectionTarget {
  mode: "bridgeVertices" | "fillFace";
  selectedVertices: Set<number>;
}

export interface EdgeLoopSelectionTarget {
  selectedEdges: Set<number>;
}

export interface VertexSelectionTarget {
  selectedVertices: Set<number>;
}

export interface ConnectVertexPathTarget extends VertexSelectionTarget {
  activeVertex: number | null;
}

export interface TopologySelectionInput {
  granularity: "vertex" | "edge" | "face";
  selectedVertices: ReadonlySet<number>;
  selectedEdges?: ReadonlySet<number> | null;
  selectedFaces?: ReadonlySet<number> | null;
  lastActiveVertex?: number | null;
  lastActiveEdge?: number | null;
  lastActiveFace?: number | null;
}

export interface FaceSelectionTarget extends VertexSelectionTarget {
  selectedFaces: Set<number>;
}

export interface FaceEdgeSelectionTarget extends VertexSelectionTarget {
  selectedFaces: Set<number>;
  selectedEdges: Set<number>;
}

export interface EdgeSeedSelectionTarget {
  seedEdge: [number, number];
}

export type ExtrudeSelectionTarget =
  | { mode: "faces"; selectedVertices: Set<number>; selectedFaces: Set<number> }
  | { mode: "edges"; selectedVertices: Set<number>; selectedEdges: Set<number> }
  | { mode: "vertices"; selectedVertices: Set<number> };

interface EdgeSelectionComponent {
  edgeCount: number;
  closed: boolean;
}

function topologyHasEdge(
  topology: PolygonTopology,
  v0: number,
  v1: number,
): boolean {
  const lo = Math.min(v0, v1);
  const hi = Math.max(v0, v1);
  const { edges } = topology;
  for (let i = 0; i < edges.length; i += 2) {
    const a = edges[i]!;
    const b = edges[i + 1]!;
    if (Math.min(a, b) === lo && Math.max(a, b) === hi) return true;
  }
  return false;
}

function analyzeSelectedEdgeComponents(
  topology: PolygonTopology,
  selectedEdges: ReadonlySet<number>,
): EdgeSelectionComponent[] {
  const vertexToEdges = new Map<number, number[]>();
  const degree = new Map<number, number>();
  const { edges } = topology;

  for (const ei of selectedEdges) {
    const a = edges[ei * 2];
    const b = edges[ei * 2 + 1];
    vertexToEdges.set(a, [...(vertexToEdges.get(a) ?? []), ei]);
    vertexToEdges.set(b, [...(vertexToEdges.get(b) ?? []), ei]);
    degree.set(a, (degree.get(a) ?? 0) + 1);
    degree.set(b, (degree.get(b) ?? 0) + 1);
  }

  const components: EdgeSelectionComponent[] = [];
  const visitedEdges = new Set<number>();

  for (const startEdge of selectedEdges) {
    if (visitedEdges.has(startEdge)) continue;
    const stack = [startEdge];
    const componentVertices = new Set<number>();
    let edgeCount = 0;

    while (stack.length > 0) {
      const ei = stack.pop()!;
      if (visitedEdges.has(ei)) continue;
      visitedEdges.add(ei);
      edgeCount++;

      const a = edges[ei * 2];
      const b = edges[ei * 2 + 1];
      componentVertices.add(a);
      componentVertices.add(b);

      for (const next of vertexToEdges.get(a) ?? []) {
        if (!visitedEdges.has(next)) stack.push(next);
      }
      for (const next of vertexToEdges.get(b) ?? []) {
        if (!visitedEdges.has(next)) stack.push(next);
      }
    }

    let closed = componentVertices.size > 0;
    for (const vi of componentVertices) {
      if ((degree.get(vi) ?? 0) !== 2) {
        closed = false;
        break;
      }
    }

    components.push({ edgeCount, closed });
  }

  return components;
}

function activeSelectionCount(selection: TopologySelectionInput): number {
  if (selection.granularity === "face")
    return selection.selectedFaces?.size ?? 0;
  if (selection.granularity === "edge")
    return selection.selectedEdges?.size ?? 0;
  return selection.selectedVertices.size;
}

function cloneSelectedVertices(selection: TopologySelectionInput): Set<number> {
  return new Set(selection.selectedVertices);
}

function requireResolvedVertexSelection(
  selection: TopologySelectionInput,
  reason: string,
): TopologyOpPreflight<VertexSelectionTarget> {
  const count = activeSelectionCount(selection);
  if (count === 0) return { ok: false, reason };
  if (selection.selectedVertices.size === 0) {
    if (import.meta.env.DEV) {
      throw new Error(
        `requireResolvedVertexSelection: ${selection.granularity} selection is non-empty but selectedVertices is empty`,
      );
    }
    return { ok: false, reason };
  }
  return {
    ok: true,
    target: { selectedVertices: cloneSelectedVertices(selection) },
  };
}

export function preflightFillSelection(
  topology: PolygonTopology,
  em: TopologySelectionInput,
): TopologyOpPreflight<FillSelectionTarget> {
  if (em.granularity === "face") {
    return { ok: false, reason: "Fill is not available in face mode." };
  }

  if (em.granularity === "edge") {
    const selectedEdges = em.selectedEdges ?? new Set<number>();
    const selectedVertices = deriveVerticesFromExactEdgeLoop(
      topology,
      new Set(selectedEdges),
    );
    return selectedVertices
      ? { ok: true, target: { mode: "fillFace", selectedVertices } }
      : {
          ok: false,
          reason: "Fill in edge mode requires one exact closed edge loop.",
        };
  }

  if (em.selectedVertices.size === 2) {
    return {
      ok: true,
      target: {
        mode: "bridgeVertices",
        selectedVertices: new Set(em.selectedVertices),
      },
    };
  }

  if (em.selectedVertices.size >= 3) {
    return {
      ok: true,
      target: {
        mode: "fillFace",
        selectedVertices: new Set(em.selectedVertices),
      },
    };
  }

  return {
    ok: false,
    reason: "Fill requires 2 selected vertices or a closed boundary loop.",
  };
}

export function preflightBridgeVerticesSelection(
  topology: PolygonTopology,
  em: TopologySelectionInput,
): TopologyOpPreflight<VertexSelectionTarget> {
  if (em.granularity !== "vertex") {
    return { ok: false, reason: "Bridge Vertices requires vertex mode." };
  }
  if (em.selectedVertices.size !== 2) {
    return {
      ok: false,
      reason: "Bridge Vertices requires exactly 2 selected vertices.",
    };
  }
  const [v0, v1] = [...em.selectedVertices];
  if (v0 == null || v1 == null) {
    return {
      ok: false,
      reason: "Bridge Vertices requires exactly 2 selected vertices.",
    };
  }
  if (topologyHasEdge(topology, v0, v1)) {
    return {
      ok: false,
      reason:
        "Bridge Vertices requires two vertices that are not already connected.",
    };
  }
  return {
    ok: true,
    target: { selectedVertices: new Set([v0, v1]) },
  };
}

export function preflightBridgeEdgeLoops(
  topology: PolygonTopology,
  em: TopologySelectionInput,
): TopologyOpPreflight<EdgeLoopSelectionTarget> {
  if (em.granularity !== "edge") {
    return { ok: false, reason: "Bridge Edge Loops requires edge mode." };
  }
  const selectedEdges = em.selectedEdges ?? new Set<number>();
  if (selectedEdges.size < 3) {
    return {
      ok: false,
      reason: "Bridge Edge Loops requires at least 3 selected edges.",
    };
  }

  const components = analyzeSelectedEdgeComponents(topology, selectedEdges);
  if (components.length !== 2 || components.some((c) => !c.closed)) {
    return {
      ok: false,
      reason: "Bridge Edge Loops requires exactly two closed edge loops.",
    };
  }
  if (components[0]?.edgeCount !== components[1]?.edgeCount) {
    return {
      ok: false,
      reason:
        "Bridge Edge Loops requires both loops to have the same edge count.",
    };
  }

  return { ok: true, target: { selectedEdges: new Set(selectedEdges) } };
}

export function preflightGridFillSelection(
  topology: PolygonTopology,
  em: TopologySelectionInput,
): TopologyOpPreflight<VertexSelectionTarget> {
  if (em.granularity === "face") {
    return { ok: false, reason: "Grid Fill is not available in face mode." };
  }

  if (em.granularity === "edge") {
    const selectedEdges = em.selectedEdges ?? new Set<number>();
    const selectedVertices = deriveVerticesFromExactEdgeLoop(
      topology,
      new Set(selectedEdges),
    );
    return selectedVertices
      ? { ok: true, target: { selectedVertices } }
      : {
          ok: false,
          reason: "Grid Fill in edge mode requires one exact closed edge loop.",
        };
  }

  if (em.selectedVertices.size >= 4) {
    return {
      ok: true,
      target: { selectedVertices: new Set(em.selectedVertices) },
    };
  }

  return {
    ok: false,
    reason: "Grid Fill requires at least 4 selected vertices.",
  };
}

export function preflightConnectVertexPath(
  em: TopologySelectionInput,
): TopologyOpPreflight<ConnectVertexPathTarget> {
  if (em.granularity !== "vertex") {
    return { ok: false, reason: "Connect Vertex Path requires vertex mode." };
  }
  if (em.selectedVertices.size < 2) {
    return {
      ok: false,
      reason: "Connect Vertex Path requires at least 2 selected vertices.",
    };
  }
  return {
    ok: true,
    target: {
      selectedVertices: new Set(em.selectedVertices),
      activeVertex:
        em.lastActiveVertex != null &&
        em.selectedVertices.has(em.lastActiveVertex)
          ? em.lastActiveVertex
          : null,
    },
  };
}

export function resolveSelectedEdgeSeed(
  topology: PolygonTopology,
  selection: TopologySelectionInput,
): [number, number] | null {
  const selectedEdges = selection.selectedEdges ?? new Set<number>();
  if (
    selection.granularity === "edge" &&
    selection.lastActiveEdge != null &&
    selectedEdges.has(selection.lastActiveEdge)
  ) {
    return edgeVertices(topology.edges, selection.lastActiveEdge);
  }
  if (selection.granularity === "edge" && selectedEdges.size === 1) {
    return edgeVertices(topology.edges, selectedEdges.values().next().value!);
  }
  if (
    selection.granularity === "vertex" &&
    selection.selectedVertices.size === 2
  ) {
    const [v0, v1] = Array.from(selection.selectedVertices);
    if (v0 != null && v1 != null && findEdgeIndex(topology, v0, v1) >= 0) {
      return [v0, v1];
    }
  }
  return null;
}

export function resolveBoundarySeedVertex(
  topology: PolygonTopology,
  selection: TopologySelectionInput,
): number | null {
  if (
    selection.lastActiveVertex != null &&
    selection.selectedVertices.has(selection.lastActiveVertex)
  ) {
    return selection.lastActiveVertex;
  }
  const edgeSeed = resolveSelectedEdgeSeed(topology, selection);
  if (edgeSeed) return edgeSeed[0];
  return selection.selectedVertices.values().next().value ?? null;
}

export function preflightSelectionForOp(
  em: TopologySelectionInput,
  reason: string,
): TopologyOpPreflight<VertexSelectionTarget> {
  return requireResolvedVertexSelection(em, reason);
}

export function preflightInsetSelection(
  topology: PolygonTopology,
  selection: TopologySelectionInput,
): TopologyOpPreflight<FaceSelectionTarget> {
  const base = requireResolvedVertexSelection(
    selection,
    "Inset Faces requires a face-backed selection.",
  );
  if (!base.ok) return base;

  const selectedFaces = resolveTopologyOpSelection(
    topology,
    base.target.selectedVertices,
    selection.granularity,
    selection.selectedEdges ?? null,
    selection.selectedFaces ?? null,
  ).selectedFaces;

  return selectedFaces.size > 0
    ? {
        ok: true,
        target: {
          selectedVertices: base.target.selectedVertices,
          selectedFaces,
        },
      }
    : { ok: false, reason: "Inset Faces requires a face-backed selection." };
}

export function preflightBevelSelection(
  topology: PolygonTopology,
  selection: TopologySelectionInput,
): TopologyOpPreflight<FaceEdgeSelectionTarget> {
  const base = requireResolvedVertexSelection(
    selection,
    "Bevel requires selected edges or faces.",
  );
  if (!base.ok) return base;

  const resolved = resolveTopologyOpSelection(
    topology,
    base.target.selectedVertices,
    selection.granularity,
    selection.selectedEdges ?? null,
    selection.selectedFaces ?? null,
  );

  if (resolved.selectedFaces.size === 0 && resolved.selectedEdges.size === 0) {
    return { ok: false, reason: "Bevel requires selected edges or faces." };
  }

  return {
    ok: true,
    target: {
      selectedVertices: base.target.selectedVertices,
      selectedFaces: resolved.selectedFaces,
      selectedEdges: resolved.selectedEdges,
    },
  };
}

export function preflightExtrudeSelection(
  topology: PolygonTopology,
  selection: TopologySelectionInput,
): TopologyOpPreflight<ExtrudeSelectionTarget> {
  const base = requireResolvedVertexSelection(
    selection,
    "Extrude requires a selection.",
  );
  if (!base.ok) return base;

  const resolved = resolveTopologyOpSelection(
    topology,
    base.target.selectedVertices,
    selection.granularity,
    selection.selectedEdges ?? null,
    selection.selectedFaces ?? null,
  );

  if (resolved.selectedFaces.size > 0) {
    return {
      ok: true,
      target: {
        mode: "faces",
        selectedVertices: base.target.selectedVertices,
        selectedFaces: resolved.selectedFaces,
      },
    };
  }

  if (selection.granularity === "edge" && resolved.selectedEdges.size > 0) {
    return {
      ok: true,
      target: {
        mode: "edges",
        selectedVertices: base.target.selectedVertices,
        selectedEdges: resolved.selectedEdges,
      },
    };
  }

  return {
    ok: true,
    target: {
      mode: "vertices",
      selectedVertices: base.target.selectedVertices,
    },
  };
}

/**
 * Manifold extrude requires an explicit face selection. Unlike regular
 * extrude — which has a vertex-and-edge fallback path so you can pull new
 * geometry from a skeleton — manifold extrude classifies each boundary edge
 * against its neighbor face's plane. Without face context there's nothing
 * to classify, so we refuse rather than silently degrading.
 */
export function preflightExtrudeManifold(
  topology: PolygonTopology,
  selection: TopologySelectionInput,
): TopologyOpPreflight<FaceSelectionTarget> {
  const base = requireResolvedVertexSelection(
    selection,
    "Manifold Extrude requires a face selection.",
  );
  if (!base.ok) return base;

  const resolved = resolveTopologyOpSelection(
    topology,
    base.target.selectedVertices,
    selection.granularity,
    selection.selectedEdges ?? null,
    selection.selectedFaces ?? null,
  );

  if (resolved.selectedFaces.size === 0) {
    return { ok: false, reason: "Manifold Extrude requires a face selection." };
  }

  return {
    ok: true,
    target: {
      selectedVertices: base.target.selectedVertices,
      selectedFaces: resolved.selectedFaces,
    },
  };
}

export function preflightEdgeLoopInsertSeed(
  topology: PolygonTopology,
  v0: number,
  v1: number,
): TopologyOpPreflight<EdgeSeedSelectionTarget> {
  if (!topologyHasEdge(topology, v0, v1)) {
    return {
      ok: false,
      reason: "Edge Loop Insert requires a seeded topology edge.",
    };
  }
  return {
    ok: true,
    target: { seedEdge: [v0, v1] },
  };
}
